// A/B test of the premium menu's nav styles (SmartMenuNavTest). Reports, per browser-tab session, where the
// visitor landed with which menus started hidden and how tall the nav drew (exposure), each other menu
// they opened (menu_view), and each item they opened (item_open). Mounted by premium_c/_index only while a test
// runs; the server drops anything it can't trust (OnlineOrders::Frame::NavTestEventsController).
import { Controller } from "@hotwired/stimulus";

// Matches NavTestEventsController::MAX_BATCH_SIZE: a full queue goes out instead of growing.
const MAX_QUEUE_SIZE = 50;

export default class extends Controller {
  static values = { endpoint: String, testId: Number, style: String };

  connect() {
    this.queue = [];
    this.flush = this.flush.bind(this);
    this.flushWhenHidden = () => {
      if (document.visibilityState === "hidden") this.flush();
    };
    document.addEventListener("visibilitychange", this.flushWhenHidden);
    window.addEventListener("pagehide", this.flush);

    // Measure once the web font has set the pills' widths and layout-c has scrolled the active pill into view.
    // Every then() on fonts.ready runs before the next frame, whichever controller connected first.
    (document.fonts?.ready ?? Promise.resolve()).then(() => requestAnimationFrame(() => this.land()));
  }

  disconnect() {
    this.flush();
    document.removeEventListener("visibilitychange", this.flushWhenHidden);
    window.removeEventListener("pagehide", this.flush);
  }

  // The first page of a tab's session is its exposure. A later page in the same tab (the menu host gives each
  // menu its own page) is a menu the visitor opened, when this host's own nav led there: a link on the
  // restaurant's website to another of its menus is not the nav at work.
  land() {
    const panel = this.element.querySelector(".oc-menu-schedule-section:not(.d-none)");
    if (!panel) return;

    const scheduleId = scheduleIdOf(panel.id);
    const key = `smart_menu_nav_test:${this.testIdValue}:${this.styleValue}`;
    const stored = readSession(key);
    this.token = stored || newToken();
    this.phone = window.matchMedia("(max-width: 767.98px)").matches;

    if (stored) {
      if (cameFromThisHost()) this.push({ kind: "menu_view", schedule_id: scheduleId });
    } else {
      const header = this.element.querySelector(".oc-sticky-header");
      this.push({
        kind: "exposure",
        schedule_id: scheduleId,
        hidden_schedule_ids: this.hiddenScheduleIds(scheduleId),
        nav_height: header ? header.offsetHeight : 0,
      });
      writeSession(key, this.token);
    }
    this.landed = true;
    this.flush();
  }

  // layout-c's smart-menu-layout-c:schedule-shown. Before landing, a switch is part of where the visitor landed.
  scheduleShown(event) {
    if (this.landed) this.push({ kind: "menu_view", schedule_id: event.detail.scheduleId });
  }

  // layout-c's smart-menu-layout-c:item-shown.
  itemShown(event) {
    this.push({ kind: "item_open", schedule_id: event.detail.scheduleId });
  }

  // Menus the visitor could not see on landing. Dropdown keeps every menu but the open one behind its button
  // (its menu pills are display:none, so measuring them would count none); the other styles hide the menu tabs
  // not wholly inside the strip, the ones a visitor has to scroll to see.
  hiddenScheduleIds(landedScheduleId) {
    const strip = this.element.querySelector(".oc-categories-scroll");
    if (!strip) return [];

    const pills = Array.from(strip.querySelectorAll('.oc-category-pill[data-category-id^="menu_schedule"]'));
    const menuButton = this.element.querySelector(".oc-menu-switch");
    if (menuButton && menuButton.offsetParent !== null) {
      return pills.map((pill) => scheduleIdOf(pill.dataset.categoryId)).filter((id) => id !== landedScheduleId);
    }

    const box = strip.getBoundingClientRect();
    return pills
      .filter((pill) => {
        const rect = pill.getBoundingClientRect();
        return rect.left < box.left - 1 || rect.right > box.right + 1 ||
          rect.top < box.top - 1 || rect.bottom > box.bottom + 1;
      })
      .map((pill) => scheduleIdOf(pill.dataset.categoryId));
  }

  push(event) {
    this.queue.push(event);
    if (this.queue.length >= MAX_QUEUE_SIZE) this.flush();
  }

  // Before landing there is no session token yet, so the queue waits for land() to send it.
  flush() {
    if (!this.token || this.queue.length === 0) return;

    const payload = JSON.stringify({
      test_id: this.testIdValue,
      style: this.styleValue,
      session_token: this.token,
      phone: this.phone,
      events: this.queue.splice(0),
    });
    if (navigator.sendBeacon && navigator.sendBeacon(this.endpointValue, new Blob([payload], { type: "application/json" }))) {
      return;
    }
    fetch(this.endpointValue, {
      method: "POST",
      body: payload,
      headers: { "Content-Type": "application/json" },
      keepalive: true,
    }).catch(() => {});
  }
}

// "menu_schedule12" (a panel id or a pill's data-category-id) is schedule 12.
function scheduleIdOf(id) {
  return Number(id.replace("menu_schedule", ""));
}

// The page's Referrer-Policy (strict-origin-when-cross-origin) sends the referrer on every same-origin link.
function cameFromThisHost() {
  try {
    return new URL(document.referrer).origin === window.location.origin;
  } catch (_error) {
    return false;
  }
}

// sessionStorage keeps a token per tab, so a tab's menu pages count as one session. It can throw (storage
// blocked, some private modes): then each page is its own session.
function readSession(key) {
  try {
    return window.sessionStorage.getItem(key);
  } catch (_error) {
    return null;
  }
}

function writeSession(key, token) {
  try {
    window.sessionStorage.setItem(key, token);
  } catch (_error) {
    // Each page counts as its own session.
  }
}

// randomUUID needs a secure context; the fallback fits the server's 8–36 word-character token.
function newToken() {
  if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 14)}`;
}
